import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTenant } from "../contexts/TenantContext";

const PrivacyPolicy = () => {
  const { tenant, getTenantLogo, getTenantName, getPrimaryColor } = useTenant();
  
  const companyName = getTenantName();
  const logoUrl = getTenantLogo();
  const primaryColor = getPrimaryColor();
  const isRoyalCert = tenant?.slug === "royalcert";
  
  // Tenant-specific content
  const tenantContent = {
    royalcert: {
      companyFullName: "RoyalCert Belgelendirme ve Gözetim Hizmetleri A.Ş.",
      email: "info@royalcert.com",
      address: "İstanbul, Türkiye"
    },
    trb: {
      companyFullName: "TRB Uluslararası Belgelendirme",
      email: "info@surdurulebilirdenetim.com",
      address: "Türkiye"
    }
  };
  
  const content = isRoyalCert ? tenantContent.royalcert : tenantContent.trb;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(to bottom right, ${primaryColor}10, white, ${primaryColor}10)` }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50" style={{ borderColor: `${primaryColor}20` }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={logoUrl} alt={companyName} className="h-10 w-auto" style={{ mixBlendMode: 'multiply' }} />
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Ana Sayfa
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8" style={{ color: primaryColor }} />
            <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>Gizlilik Politikası</h1>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-sm text-gray-500 mb-6">Son güncelleme: 15 Şubat 2026</p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>1. Giriş</h2>
              <p>
                {content.companyFullName} ("biz", "bizim" veya "Şirket") olarak, gizliliğinize önem veriyoruz. 
                Bu Gizlilik Politikası, hizmetlerimizi kullanırken topladığımız, kullandığımız ve 
                koruduğumuz kişisel verileriniz hakkında sizi bilgilendirmek amacıyla hazırlanmıştır.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>2. Topladığımız Veriler</h2>
              <p>Hizmetlerimizi kullanırken aşağıdaki verileri toplayabiliriz:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Kimlik Bilgileri:</strong> Ad, soyad, e-posta adresi</li>
                <li><strong>Hesap Bilgileri:</strong> Google hesabı üzerinden sağlanan profil bilgileri</li>
                <li><strong>İş Verileri:</strong> Denetim raporları, müşteri bilgileri, takvim etkinlikleri</li>
                <li><strong>Teknik Veriler:</strong> IP adresi, tarayıcı türü, erişim zamanları</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>3. Google Hizmetleri Entegrasyonu</h2>
              <p>
                Uygulamamız, Google Calendar ve Google Drive hizmetleriyle entegre çalışmaktadır. 
                Bu entegrasyonlar kapsamında:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li><strong>Google Calendar:</strong> Denetim etkinliklerini takviminize eklemek için kullanılır</li>
                <li><strong>Google Drive:</strong> Onaylanan raporları depolamak için kullanılır</li>
              </ul>
              <p className="mt-3">
                Google hizmetlerine erişim izni verdiğinizde, sadece belirtilen amaçlar için 
                gerekli minimum verilere erişiriz. Verileriniz üçüncü taraflarla paylaşılmaz.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>4. Verilerin Kullanımı</h2>
              <p>Topladığımız verileri aşağıdaki amaçlarla kullanırız:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Hizmetlerimizi sağlamak ve iyileştirmek</li>
                <li>Kullanıcı hesaplarını yönetmek</li>
                <li>Denetim süreçlerini organize etmek</li>
                <li>Raporlama ve belgeleme işlemlerini gerçekleştirmek</li>
                <li>Yasal yükümlülüklerimizi yerine getirmek</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>5. Veri Güvenliği</h2>
              <p>
                Verilerinizin güvenliğini sağlamak için endüstri standardı güvenlik önlemleri 
                kullanıyoruz. Bunlar arasında SSL şifreleme, güvenli sunucu altyapısı ve 
                düzenli güvenlik güncellemeleri yer almaktadır.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>6. Veri Saklama</h2>
              <p>
                Kişisel verilerinizi, hizmetlerimizi sağlamak için gerekli olduğu sürece veya 
                yasal yükümlülüklerimiz gerektirdiği sürece saklarız. Hesabınızı sildiğinizde, 
                verileriniz makul bir süre içinde sistemlerimizden kaldırılır.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>7. Haklarınız</h2>
              <p>KVKK kapsamında aşağıdaki haklara sahipsiniz:</p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
                <li>Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme</li>
                <li>Kişisel verilerinizin düzeltilmesini isteme</li>
                <li>Kişisel verilerinizin silinmesini veya yok edilmesini isteme</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>8. İletişim</h2>
              <p>
                Gizlilik politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:
              </p>
              <p className="mt-3">
                <strong>E-posta:</strong> {content.email}<br />
                <strong>Adres:</strong> {content.address}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: primaryColor }}>9. Değişiklikler</h2>
              <p>
                Bu Gizlilik Politikası'nı zaman zaman güncelleyebiliriz. Önemli değişiklikler 
                yapıldığında sizi bilgilendireceğiz. Bu sayfayı düzenli olarak kontrol etmenizi 
                öneririz.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12 text-white" style={{ backgroundColor: primaryColor }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-white/70">
            © 2026 {content.companyFullName}. Tüm hakları saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
